import React, { useEffect, useState, useCallback } from "react";
import { 
    MessageSquare,
    FileText,
    Lightbulb,
    Bell,
    Settings,
    User,
    Scale,
    Award
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import httpClient from "../Api/axios";

// Import the actual Page components
import ProfilePage from "./ProfilePage";
import CommentsPage from "./CommentsPage";
import Notification from "./Notification"; 
import QuizPage from "./QuizPage";
import Legal from "./Legal";
import CertificationPage from "./CertificationPage";

const UserDashboard = () => {
    const navigate = useNavigate();
    const [profile, setProfile] = useState({ 
        userId: "",
        userName: "", 
        userEmail: "", 
        bio: "", 
        profilePhoto: null,
        githubAcc: "",
        workProfile: "",
        userRole: "",
        showWorkProfile: false,
        showGithub: false,
        userDOB: "",
        userGender: ""
    });
    const [loading, setLoading] = useState(true);
    const [showLogoutConfirm, setShowLogoutConfirm] = useState(false); 
    
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 1024);
    const [activeLink, setActiveLink] = useState("/certifications"); 
    const email = localStorage.getItem("userEmail");

    const fetchProfile = useCallback(async () => {
        if (!email) return;
        try {
            const res = await httpClient.get(`/api/v1/signups/profile/${email}`);
            if (res.status === 200 && res.data) {
                const fetchedUserId = res.data.id || res.data.userId || "";
                
                if (fetchedUserId) localStorage.setItem("userId", fetchedUserId);

                const userData = {
                    userId: fetchedUserId,
                    userName: res.data.userName || "User",
                    userEmail: res.data.userEmail || email,
                    bio: res.data.bio || "",
                    profilePhoto: res.data.userProfile || null,
                    githubAcc: res.data.githubAcc || "",
                    workProfile: res.data.collegeName || "", 
                    userRole: res.data.userRole || "",
                    showWorkProfile: res.data.showWorkProfile || false,
                    showGithub: res.data.showGithub || false,
                    userDOB: res.data.userDOB || "",
                    userGender: res.data.userGender || ""
                };
                setProfile(userData);
                localStorage.setItem("userName", userData.userName);
            }
        } catch (error) {
            console.error("Error fetching profile:", error);
        } finally {
            setLoading(false);
        }
    }, [email]);

    useEffect(() => {
        if (window.location.hash) {
            setActiveLink(window.location.hash.replace("#", ""));
        } else {
            setActiveLink("/certifications");
        }
    }, []);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= 1024);
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    useEffect(() => {
        fetchProfile();
    }, [fetchProfile]);

    const handleLogout = () => {
        localStorage.clear();
        navigate("/", { replace: true });
    };

    const handleNavLinkClick = (path) => {
        setActiveLink(path);
        window.location.hash = path; 
    };

    const renderMainContent = () => {
        switch (activeLink) {
            case "/comments":
                return <CommentsPage />;
            case "/projects":
                return <div style={{padding: '2rem', color: '#e5e5e5'}}><h3>Projects Section</h3><p>Content for /projects.</p></div>;
            case "/quiz":
                return <QuizPage />;
            case "/certifications":
                return <CertificationPage userId={profile.userId} />;
            case "/notifications":
                return <Notification userId={profile.userId} />;
            case "/settings":
                return <div style={{padding: '2rem', color: '#e5e5e5'}}><h3>Settings Section</h3><p>Content for /settings.</p></div>;
            case "/legal":
                return <Legal />;
            case "/profile":
                return (
                    <ProfilePage 
                        profile={profile} 
                        setProfile={setProfile} 
                        handleLogout={handleLogout} 
                        refreshProfile={fetchProfile} 
                    />
                );
            default:
                return (
                    <div style={{padding: '2rem', color: '#e5e5e5'}}>
                        <h2>Welcome, {loading ? "..." : profile.userName}!</h2>
                        <p>Select an option from the menu.</p>
                    </div>
                );
        }
    };

    return (
        <>
            <style>{`
                html, body, #root { 
                    height: 100%; 
                    margin: 0; 
                    padding: 0; 
                    background: #0f0f10; 
                }
                
                .dashboard-container { 
                    display: flex; 
                    min-height: 100vh; 
                    color: #fff; 
                    font-family: Inter, sans-serif; 
                }
                
                /* Sidebar - Fixed structure */
                .sidebar { 
                    width: 18rem; 
                    background: #111113; 
                    display: flex; 
                    flex-direction: column; 
                    border-right: 1px solid #222326; 
                    flex-shrink: 0;
                    height: 100vh;
                    position: sticky;
                    top: 0;
                    overflow: hidden;
                }
                
                /* Scrollable menu area */
                .sidebar-menu-container {
                    flex: 1;
                    overflow-y: auto;
                    padding: 1.5rem 1rem 1rem 1rem;
                    scrollbar-width: thin;
                    scrollbar-color: #5a54eb #222326;
                }
                
                .sidebar-menu-container::-webkit-scrollbar {
                    width: 5px;
                }
                
                .sidebar-menu-container::-webkit-scrollbar-track {
                    background: #222326;
                    border-radius: 10px;
                }
                
                .sidebar-menu-container::-webkit-scrollbar-thumb {
                    background: #5a54eb;
                    border-radius: 10px;
                }
                
                /* Fixed bottom section */
                .sidebar-bottom-fixed {
                    padding: 1rem;
                    border-top: 1px solid #222326;
                    background: #111113;
                    flex-shrink: 0;
                }
                
                .logo { 
                    font-size: 1.6rem; 
                    font-weight: 700; 
                    margin-bottom: 1.5rem; 
                    color: #e5e5e5; 
                    white-space: nowrap; 
                    overflow: visible; 
                    padding: 0 0.5rem;
                }
                
                .menu { 
                    list-style: none; 
                    margin: 0; 
                    padding: 0; 
                    display: grid; 
                    gap: 0.375rem; 
                }
                
                .menu-item, .logout-button { 
                    position: relative; 
                    display: flex; 
                    align-items: center; 
                    gap: 0.6rem; 
                    padding: 0.56rem 0.7rem; 
                    border-radius: 0.65rem; 
                    color: #cfcfd6; 
                    background: transparent; 
                    text-decoration: none; 
                    cursor: pointer; 
                    transition: all 0.25s ease; 
                    overflow: hidden; 
                    border: 1px solid transparent; 
                    width: 100%; 
                    text-align: left; 
                    box-sizing: border-box; 
                }
                
                .menu-item:hover, .logout-button:hover { 
                    color: #fff; 
                    background: #333333; 
                }
                
                .menu-item.is-active { 
                    color: #111113; 
                    background: #ffffff; 
                    box-shadow: 0 0 5px rgba(0,0,0,0.2); 
                }
                
                .profile-top-divider { 
                    margin: 0 0 1rem 0; 
                    border: none; 
                    height: 1px; 
                    background: rgba(255,255,255,0.06); 
                }
                
                .profile-card { 
                    display: flex; 
                    align-items: center; 
                    gap: .75rem; 
                    padding: .75rem .9rem; 
                    border-radius: .9rem; 
                    background: #141416; 
                    cursor: pointer; 
                    transition: all 0.25s ease; 
                    border: 1px solid transparent; 
                    margin-bottom: 0.75rem;
                }
                
                .profile-card:hover { 
                    background: #1e1e20; 
                    color: #fff; 
                }
                
                .profile-card.is-active { 
                    background: #ffffff; 
                    border: 1px solid #ffffff; 
                    color: #111113; 
                }
                
                .profile-avatar-initials { 
                    width: 36px; 
                    height: 36px; 
                    border-radius: 50%; 
                    background: #2a2a31; 
                    color: #e8e8f0; 
                    font-weight: 700; 
                    display: flex; 
                    align-items: center; 
                    justify-content: center; 
                    overflow: hidden; 
                    flex-shrink: 0;
                }
                
                .profile-avatar-img { 
                    width: 100%; 
                    height: 100%; 
                    object-fit: cover; 
                }
                
                .profile-info { 
                    display: flex; 
                    flex-direction: column; 
                    min-width: 0; 
                    flex: 1;
                }
                
                .profile-name { 
                    font-weight: 700; 
                    font-size: .92rem; 
                    margin: 0; 
                    overflow: hidden; 
                    text-overflow: ellipsis; 
                    white-space: nowrap; 
                }
                
                .profile-email { 
                    color: #a5a6ad; 
                    font-size: .78rem; 
                    margin: 2px 0 0 0; 
                    overflow: hidden; 
                    text-overflow: ellipsis; 
                    white-space: nowrap; 
                }
                
                .profile-caret { 
                    margin-left: auto; 
                    color: #a7a8b0; 
                    opacity: .9; 
                    flex-shrink: 0;
                }
                
                .logout-button { 
                    margin-top: 0;
                    background: rgba(255, 77, 79, 0.1);
                    color: #ff6b6b;
                }
                
                .logout-button:hover { 
                    background: rgba(255, 77, 79, 0.2);
                    color: #ff8888;
                }
                
                .logout-confirm { 
                    margin-top: 0.5rem; 
                    padding: 0.5rem; 
                    border-radius: 8px; 
                    background: rgba(255, 77, 79, 0.15);
                }
                
                .confirm-logout-btn {
                    background: #ff4d4f;
                    color: white;
                    border: none;
                    padding: 0.5rem;
                    border-radius: 6px;
                    cursor: pointer;
                    width: 100%;
                    font-weight: 600;
                    transition: all 0.2s ease;
                }
                
                .confirm-logout-btn:hover {
                    background: #ff3335;
                    transform: translateY(-1px);
                }
                
                .main-content { 
                    flex: 1; 
                    background: #0f0f10; 
                    overflow: auto; 
                    display: flex; 
                    flex-direction: column; 
                }
                
                /* Mobile styles */
                @media (max-width: 1024px) {
                    .sidebar { 
                        display: none; 
                    }
                    
                    .main-content { 
                        padding-bottom: 6rem; 
                    }
                    
                    .mobile-header { 
                        display: flex; 
                        justify-content: space-between; 
                        align-items: center; 
                        padding: 1rem; 
                        background: #0f0f10; 
                        border-bottom: 1px solid #222326; 
                        position: sticky; 
                        top: 0; 
                        z-index: 50; 
                    }
                    
                    .mobile-logo { 
                        color: #e5e5e5; 
                        font-weight: 700; 
                        font-size: 1.5rem; 
                    }
                    
                    .nav-bottom { 
                        position: fixed; 
                        bottom: 0; 
                        left: 0; 
                        width: 100%; 
                        background: #111113; 
                        display: flex; 
                        justify-content: space-around; 
                        padding: 0.8rem 0; 
                        border-top: 1px solid #222326; 
                        z-index: 100; 
                    }
                    
                    .nav-item { 
                        color: #a5a6ad; 
                        cursor: pointer; 
                        display: flex; 
                        flex-direction: column; 
                        align-items: center; 
                        justify-content: center; 
                        transition: color 0.2s ease;
                    }
                    
                    .nav-item.is-active { 
                        color: #ffffff; 
                    }
                }
            `}</style>
            
            {isMobile && (
                <div className="mobile-header">
                    <span className="mobile-logo">CommunityofHubs</span>
                    <div style={{ display: 'flex', gap: '10px' }}>
                        <button className="mobile-certif-btn" onClick={() => handleNavLinkClick("/certifications")} style={{background:'none', border:'none', cursor: 'pointer'}}>
                            <Award size={26} color={activeLink === "/certifications" ? "#fff" : "#cfcfd6"} />
                        </button>
                        <button className="mobile-settings-btn" onClick={() => handleNavLinkClick("/settings")} style={{background:'none', border:'none', cursor: 'pointer'}}>
                            <Settings size={26} color={activeLink === "/settings" ? "#fff" : "#cfcfd6"} />
                        </button>
                        <button className="mobile-notif-btn" onClick={() => handleNavLinkClick("/notifications")} style={{background:'none', border:'none', cursor: 'pointer'}}>
                            <Bell size={26} color={activeLink === "/notifications" ? "#fff" : "#cfcfd6"} />
                        </button>
                    </div>
                </div>
            )}

            <div className="dashboard-container">
                {/* Desktop Sidebar */}
                {!isMobile && (
                    <div className="sidebar">
                        {/* Scrollable Menu Area */}
                        <div className="sidebar-menu-container">
                            <h1 className="logo">CommunityofHubs</h1>
                            <ul className="menu">
                                <li>
                                    <div
                                        onClick={() => handleNavLinkClick("/comments")}
                                        className={`menu-item ${activeLink === "/comments" ? "is-active" : ""}`}
                                    >
                                        <MessageSquare size={20} />
                                        <span>Comments</span>
                                    </div>
                                </li>
                                <li>
                                    <div
                                        onClick={() => handleNavLinkClick("/projects")}
                                        className={`menu-item ${activeLink === "/projects" ? "is-active" : ""}`}
                                    >
                                        <FileText size={20} />
                                        <span>Projects</span>
                                    </div>
                                </li>
                                <li>
                                    <div
                                        onClick={() => handleNavLinkClick("/quiz")}
                                        className={`menu-item ${activeLink === "/quiz" ? "is-active" : ""}`}
                                    >
                                        <Lightbulb size={20} />
                                        <span>Quiz</span>
                                    </div>
                                </li>
                                <li>
                                    <div
                                        onClick={() => handleNavLinkClick("/certifications")}
                                        className={`menu-item ${activeLink === "/certifications" ? "is-active" : ""}`}
                                    >
                                        <Award size={20} />
                                        <span>Certificates</span>
                                    </div>
                                </li>
                                <li>
                                    <div
                                        onClick={() => handleNavLinkClick("/notifications")}
                                        className={`menu-item ${activeLink === "/notifications" ? "is-active" : ""}`}
                                    >
                                        <Bell size={20} />
                                        <span>Notifications</span>
                                    </div>
                                </li>
                                <li>
                                    <div
                                        onClick={() => handleNavLinkClick("/settings")}
                                        className={`menu-item ${activeLink === "/settings" ? "is-active" : ""}`}
                                    >
                                        <Settings size={20} />
                                        <span>Settings</span>
                                    </div>
                                </li>
                                <li>
                                    <div
                                        onClick={() => handleNavLinkClick("/legal")}
                                        className={`menu-item ${activeLink === "/legal" ? "is-active" : ""}`}
                                    >
                                        <Scale size={20} />
                                        <span>Legal</span>
                                    </div>
                                </li>
                            </ul>
                        </div>
                        
                        {/* Fixed Bottom Section - Profile & Logout */}
                        <div className="sidebar-bottom-fixed">
                            <hr className="profile-top-divider" />
                            <div 
                                className={`profile-card ${activeLink === "/profile" ? "is-active" : ""}`} 
                                onClick={() => handleNavLinkClick("/profile")}
                            >
                                <span className="profile-avatar-initials">
                                    {profile.profilePhoto ? (
                                        <img src={profile.profilePhoto} className="profile-avatar-img" alt="User" />
                                    ) : (
                                        ((profile.userName && profile.userName[0]) || "U").toUpperCase()
                                    )}
                                </span>
                                <div className="profile-info">
                                    <p className="profile-name">{loading ? "Loading..." : profile.userName}</p>
                                    <p className="profile-email">{profile.userEmail}</p>
                                </div>
                                <User size={18} className="profile-caret" />
                            </div>

                            <button className="logout-button" onClick={() => setShowLogoutConfirm(!showLogoutConfirm)}>
                                <User size={20} />
                                <span>Logout</span>
                            </button>
                            
                            {showLogoutConfirm && (
                                <div className="logout-confirm">
                                    <button className="confirm-logout-btn" onClick={handleLogout}>
                                        Confirm Logout
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Main Content */}
                <div className="main-content">
                    {renderMainContent()}
                </div>

                {/* Mobile Bottom Navigation */}
                {isMobile && (
                    <nav className="nav-bottom">
                        <div onClick={() => handleNavLinkClick("/comments")} className={`nav-item ${activeLink === "/comments" ? "is-active" : ""}`}>
                            <MessageSquare size={24} />
                        </div>
                        <div onClick={() => handleNavLinkClick("/projects")} className={`nav-item ${activeLink === "/projects" ? "is-active" : ""}`}>
                            <FileText size={24} />
                        </div>
                        <div onClick={() => handleNavLinkClick("/quiz")} className={`nav-item ${activeLink === "/quiz" ? "is-active" : ""}`}>
                            <Lightbulb size={24} />
                        </div>
                        <div onClick={() => handleNavLinkClick("/certifications")} className={`nav-item ${activeLink === "/certifications" ? "is-active" : ""}`}>
                            <Award size={24} />
                        </div>
                        <div onClick={() => handleNavLinkClick("/notifications")} className={`nav-item ${activeLink === "/notifications" ? "is-active" : ""}`}>
                            <Bell size={24} />
                        </div>
                        <div onClick={() => handleNavLinkClick("/profile")} className={`nav-item ${activeLink === "/profile" ? "is-active" : ""}`}>
                            {profile.profilePhoto ? (
                                <img src={profile.profilePhoto} alt="Profile" style={{ width: '26px', height: '26px', borderRadius: '50%', border: activeLink === "/profile" ? '2px solid white' : '2px solid transparent'}} />
                            ) : (
                                <User size={24} />
                            )}
                        </div>
                    </nav>
                )}
            </div>
        </>
    );
};

export default UserDashboard;